import React, { useState, useMemo, useEffect, useCallback, SetStateAction, Dispatch } from 'react'
import { Pagination } from '../pagination/pagination'
import { SearchInput } from './SearchInput'
import { Translates } from '@/services/repository/i18n'
import { Spinner } from '../spinner'

import './SearchTable.css'
import { initialSort, sortRows } from './useTableSort'
import type { SortQuery, SortValues } from './useTableSort'

export interface StaticPaginatedTableState {
    pageNumber: number,
    pageSize: number
}

export interface StaticPaginatedTableParams<T> {
    data: T[]
    toolboxLeft?: () => React.ReactElement
    toolboxRight?: () => React.ReactElement
    header?: (query: SortQuery, onSort: (field: string) => void) => React.ReactElement
    sortValues?: SortValues<T>
    row: (
        row: T,
        currentPage: T[],
        setPageRows: React.Dispatch<React.SetStateAction<T[]>>
    ) => React.ReactElement
    // initialState?: {
    //     pageSize: number
    // }
    parentState?: StaticPaginatedTableState
    setParentState?: Dispatch<SetStateAction<StaticPaginatedTableState>>
    translates: Translates
    className?: string
    filterFn?: (row: T, search: string) => boolean // Optional: customize search
}

export function StaticPaginatedTable<T>({
    data,
    row,
    header,
    toolboxLeft,
    toolboxRight,
    translates,
    className,
    filterFn,
    parentState,
    setParentState,
    sortValues
    // initialState
}: StaticPaginatedTableParams<T>) {
    const [localState, setLocalState] = useState<StaticPaginatedTableState>({
        pageNumber: 1,
        pageSize: 30
    })
    const [sortQuery, setSortQuery] = useState<SortQuery>(() => initialSort(sortValues))

    // Determine whether component is controlled by parent
    const isControlled = parentState !== undefined && setParentState !== undefined;

    // Use parent state if controlled, otherwise local state
    const state = isControlled ? parentState : localState;
    const setState = isControlled ? setParentState : setLocalState;

    const paginatedRows = useMemo(() => {
        const rows = sortRows(data, sortQuery, sortValues)
        const page = Math.min(state.pageNumber, Math.max(1, Math.ceil(rows.length / state.pageSize)))
        return rows.slice((page - 1) * state.pageSize, page * state.pageSize)
    }, [data, state, sortQuery, sortValues])

    const totalPages = useMemo(() => {
        return Math.max(1, Math.ceil(data.length / state.pageSize))
    }, [data, state])

    if(paginatedRows === undefined || totalPages === undefined) {
        return <Spinner />
    }

    const handlePageChange = (next: { pageSize: number; pageNumber: number }) => {
        setState(prev => ({
            ...prev,
            pageSize: next.pageSize,
            pageNumber: next.pageNumber,
        }))
    }

    const handleSort = (field: string) => {
        if (!sortValues?.[field]) return
        setSortQuery(q => ({
            sortBy: field,
            sortDirection: q.sortBy === field && q.sortDirection === 'asc' ? 'desc' : 'asc',
        }))
        setState(previous => ({ ...previous, pageNumber: 1 }))
    }

    return (
        <div className={className || 'main-search-table'}>
            {toolboxLeft || toolboxRight ? <div className="toolbox">
                <div className="left">{toolboxLeft && toolboxLeft()}</div>
                <div className="right">{toolboxRight && toolboxRight()}</div>
            </div> : null}

            {paginatedRows.length ? <Pagination
                translates={translates}
                pageQuery={{
                    pageSize: state.pageSize,
                    pageNumber: Math.min(state.pageNumber, totalPages),
                    totalPages
                }}
                onChange={handlePageChange}
            /> : null}

            <div className="search-body-wrap">
                <div className="search-body">
                    {header && <div className="row header">{header(sortQuery, handleSort)}</div>}
                    {paginatedRows.map(item => (
                        <div className="row" key={(item as any).id}>
                            {row(item, paginatedRows, () => { })}
                        </div>
                    ))}
                </div>
            </div>
            {!paginatedRows.length && (
                <div className="empty-search-table-message">
                    {translates.for('elements.search.empty_table')}
                </div>
            )}

            {paginatedRows.length ? <Pagination
                translates={translates}
                pageQuery={{
                    pageSize: state.pageSize,
                    pageNumber: Math.min(state.pageNumber, totalPages),
                    totalPages
                }}
                onChange={handlePageChange}
            /> : null}
        </div>
    )
}
